import type { ObjectiveGradingScale } from 'react-common'
import { request } from 'react-common'
import { Activity } from '@/api/models/activity'
import { Dictionary } from '@/utils'
import { ActivityPartId, ActivityWorkflowId, ObjectiveWorkflowId } from './aggregate'
import { ShowcaseModel } from './showcase'

export type ObjectiveId = string
export interface Objective {
  id: ObjectiveId
  code: string
  name: string
  description: string
  imageUrl?: string | null
  isInternalImage: boolean
  activity: Activity
  about?: string
  aboutContent?: string
  showcases?: ShowcaseModel[]
  tenant?: any
  objectiveGradingScale: ObjectiveGradingScale
  passingType: ObjectivePassingType
  announcementChannelId: string
}

export enum ObjectivePassingType {
  FlowControl = 1,
  GradingScale = 2,
}

export type ResourceInitRequestModel = {
  authUri: string
  iss: string
  targetLinkUri: string
  loginHint: string
  ltiMessageHint: string
}

export type ObjectiveResourceToken = {
  token: string
}

export type ObjectiveResourceForm = {
  form: { [key: string]: string }
}

export class ObjectiveApi {
  private static readonly objectiveWorkflowsResourceInitRequest = request(
    'objective-workflows/:objectiveWorkflowsId/resource-link',
  )
  private static readonly objectiveWorkflowsResourceForms = request(
    'objective-workflows/:objectiveWorkflowsId/resource-form/:activityWorkflowIds',
  )

  static getObjectiveWorkflowsResourceInitRequest(
    objectiveWorkflowsId: ObjectiveWorkflowId,
    activityWorkflowIds: ActivityWorkflowId[],
    parameters: Dictionary<unknown> = {},
    activityPartIds: ActivityPartId[] = [],
  ) {
    return this.objectiveWorkflowsResourceInitRequest
      .post({
        url: { objectiveWorkflowsId },
        body: { activityWorkflowIds, activityPartIds },
        query: parameters,
      })
      .then((res) => {
        const model = res.body as ResourceInitRequestModel
        model.targetLinkUri = new URL(model.targetLinkUri, document.location.href).toString()
        // if targetLinkUri is absolute then href is ignored           -> targetLinkUri
        // if targetLinkUri is relative then a path of href is ignored -> href.origin + targetLinkUri
        // if targetLinkUri is empty, undefined, null                  -> unsupported case
        return model
      })
  }

  static getActivityResourceForm(
    objectiveWorkflowsId: ObjectiveWorkflowId,
    activityWorkflowIds: ActivityWorkflowId[],
    parameters: Dictionary<unknown> = {},
  ) {
    return this.objectiveWorkflowsResourceForms
      .get({
        url: { objectiveWorkflowsId, activityWorkflowIds: encodeURIComponent(activityWorkflowIds.join('/')) },
        query: parameters,
      })
      .then((res) => res.body as ObjectiveResourceForm)
  }
}
